import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Cart } from 'src/app/common/Cart';
import { OrderGroup } from 'src/app/common/OrderGroup';
import {
  OrderPositionValidationError,
  OrderValidationProblem,
} from 'src/app/common/OrderValidation';
import { reservationFailureText } from 'src/app/common/ReservationFailure';
import { ReservationStatus } from 'src/app/common/ReservationStatus';
import { CartService } from 'src/app/services/cart.service';
import { CartItemComponent } from '../cart-item/cart-item.component';
import { CommonModule } from '@angular/common';
import { AsyncClickDirective } from '../../directives/async-click.directive';
import { Observable, tap } from 'rxjs';
import { parseApiProblem } from '../../common/ApiError';

@Component({
    imports: [CommonModule, CartItemComponent, AsyncClickDirective],
    selector: 'app-cart',
    templateUrl: './cart.component.html',
    styleUrls: ['./cart.component.css']
})
export class CartComponent implements OnInit {
  private cartService = inject(CartService);
  private toastr = inject(ToastrService);
  private router = inject(Router);

  private destroyRef = inject(DestroyRef);

  public cart: Cart;

  // Backend #507 (G2): per-position rejection reasons from the last 422 checkout attempt,
  // keyed by skuId. Set when the order gate rejects, cleared on the next attempt (edits in
  // between don't clear them — the marks explain why the LAST attempt failed until retried).
  public positionErrors: Record<string, OrderPositionValidationError> = {};

  ngOnInit(): void {
    if (!this.cartService.cart) {
      this.cartService.reloadCart();
    }

    this.cartService.$cart.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((cart) => {
      this.cart = cart;
    });
  }

  // #456: checkout now reports the reservation outcome on the returned OrderGroup. Reserved → order
  // placed; PendingReservation → still processing (the order-detail view polls for the outcome);
  // Failed → say why, cart is kept for retry. A 409 means a checkout is already in flight.
  // Bound as [asyncClick]="checkout.bind(this)"; the directive owns the in-flight
  // guard (formerly the `checkingOut` flag) and the button also disables while the
  // cart reports isCheckoutPending. tap keeps the reservation-outcome branching;
  // the error is re-thrown so the directive resets its busy state.
  public checkout(): Observable<unknown> {
    this.positionErrors = {};
    return this.cartService.checkout().pipe(
      tap({
        next: (orderGroup: OrderGroup) => {
          switch (orderGroup?.reservationStatus) {
            case ReservationStatus.Reserved:
              this.toastr.success($localize`Ihr Kauf ist erfolgreich abgeschlossen`);
              this.goToOrder(orderGroup.id);
              break;
            case ReservationStatus.Failed:
              // Backend #706: the code, in words. The prose beside it is English with a SKU id —
              // diagnostics, so it goes to the console and never into the toast.
              console.warn(
                'Reservation failed',
                orderGroup.reservationFailureCode,
                orderGroup.reservationFailureReason
              );
              this.toastr.error(
                reservationFailureText(orderGroup) ??
                  $localize`Die Reservierung ist fehlgeschlagen. Bitte versuchen Sie es erneut.`
              );
              break;
            case ReservationStatus.PendingReservation:
              this.toastr.info($localize`Ihre Bestellung wird bearbeitet …`);
              this.goToOrder(orderGroup.id);
              break;
            // A status this build does not know yet (or one the backend omitted) is NOT
            // evidence of a completed purchase — only an explicit Reserved is. Announce the
            // submission neutrally and hand over to the order view, which polls the outcome.
            //
            // #168: until the wire format was fixed this was the ONLY branch that ever ran —
            // reservationStatus arrives as a camelCase string and every case above compared it
            // against a number. The bug was invisible precisely because this branch does
            // something plausible.
            case ReservationStatus.Unknown:
            default:
              this.toastr.info(
                $localize`Ihre Bestellung wurde übermittelt. Bitte prüfen Sie den Status der Bestellung.`
              );
              this.goToOrder(orderGroup?.id);
              break;
          }
        },
        error: (err) => {
          if (err?.status === 409) {
            this.toastr.info($localize`Für diesen Warenkorb läuft bereits ein Checkout.`);
            this.goToOrder(this.getPendingOrderGroupId(err));
          } else if (err?.status === 422 && this.applyPositionErrors(err)) {
            // Backend #507 (G2): the order gate rejected the whole order. Mark the offending
            // lines (the buyer removes/fixes them and re-submits) and re-read the cart so
            // server-side flags (unavailableReason) and prices are current.
            this.toastr.error(
              $localize`Einige Artikel sind nicht mehr bestellbar. Bitte prüfen Sie die markierten Positionen.`
            );
            this.cartService.reloadCart();
          } else {
            this.toastr.error($localize`Beim Kauf ist ein Fehler aufgetreten`);
          }
        },
      })
    );
  }

  // Parses the forwarded 422 body ({ positions: [{ skuId, reason, ... }] }) and indexes it by
  // skuId. Returns false when the body carries no positions (e.g. a non-gate 422), so the
  // caller can fall back to the generic error toast.
  private applyPositionErrors(err: any): boolean {
    // parseApiProblem handles the BFF's verbatim body either way: parsed or double-encoded.
    const body = parseApiProblem<OrderValidationProblem>(err);
    if (!body?.positions?.length) {
      return false;
    }

    this.positionErrors = {};
    for (const position of body.positions) {
      this.positionErrors[position.skuId] = position;
    }
    return true;
  }

  private goToOrder(orderGroupId: string): void {
    if (orderGroupId) {
      this.router.navigateByUrl(`/shop-order-group-edit/${orderGroupId}`);
    }
  }

  private getPendingOrderGroupId(err: any): string | undefined {
    return parseApiProblem<{ pendingOrderGroupId?: string }>(err)?.pendingOrderGroupId;
  }
}
